import { useState, useRef, useEffect, useCallback } from 'react';
import { useStore } from '../store';
import { generateApi } from '../services/api';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Sparkles,
  Lightbulb,
  HelpCircle,
  CheckCircle,
  Maximize2,
  Minimize2,
} from 'lucide-react';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

type WidgetSize = 'small' | 'medium' | 'large';
type ChatMode = 'planning' | 'content';

const SIZE_CLASSES: Record<WidgetSize, string> = {
  small: 'w-80 h-96',
  medium: 'w-96 h-[28rem]',
  large: 'w-[28rem] h-[36rem]',
};

const POSITION_CLASSES: Record<string, string> = {
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
};

const QUICK_ACTIONS = [
  { id: 'improve', label: 'Improve', icon: Sparkles, prompt: 'Improve the writing quality, clarity, and professionalism of the current content.' },
  { id: 'ideas', label: 'Ideas', icon: Lightbulb, prompt: 'Suggest creative ideas and enhancements for this document section.' },
  { id: 'explain', label: 'Explain', icon: HelpCircle, prompt: 'Explain the current document content in simpler terms and provide context.' },
  { id: 'check', label: 'Check', icon: CheckCircle, prompt: 'Check the content for grammar, spelling, consistency, and factual accuracy.' },
];

export default function ChatWidget() {
  const { chatSettings, sections, applicantData, systemPrompt, genericSections } = useStore();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [size, setSize] = useState<WidgetSize>('medium');
  const [mode, setMode] = useState<ChatMode>('content');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const position = chatSettings.position || 'bottom-right';

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Listen for 'open-chat' events from section buttons
  useEffect(() => {
    const handleOpenChat = (e: Event) => {
      const customEvent = e as CustomEvent;
      setIsOpen(true);
      if (customEvent.detail?.message) {
        // Send the message automatically
        setTimeout(() => {
          sendMessage(customEvent.detail.message);
        }, 100);
      }
    };

    window.addEventListener('open-chat', handleOpenChat);
    return () => window.removeEventListener('open-chat', handleOpenChat);
  }, [sections, applicantData, genericSections, systemPrompt]);

  const cycleSize = () => {
    const sizes: WidgetSize[] = ['small', 'medium', 'large'];
    const currentIndex = sizes.indexOf(size);
    setSize(sizes[(currentIndex + 1) % sizes.length]);
  };

  const buildContext = (): string => {
    const sectionsSummary = sections
      .map((s) => `[${s.title}]: ${s.content ? s.content.substring(0, 200) + '...' : '(empty)'}`)
      .join('\n');

    // Build document data summary from genericSections
    const documentDataSummary = genericSections
      .filter((gs) => gs.fields.some((f) => f.value))
      .map((gs) => {
        const fields = gs.fields
          .filter((f) => f.value)
          .map((f) => `  ${f.label}: ${f.value}`)
          .join('\n');
        return `[${gs.title}]:\n${fields}`;
      })
      .join('\n');

    if (mode === 'planning') {
      return `You are helping plan and structure a document. Current sections: ${sections.length}. Document type context available. Help the user plan their document structure, suggest sections, and organize content.\n\nDocument Data:\n${documentDataSummary}`;
    }

    return `You are an AI writing assistant helping with document content. Here is the current document context:\n\nDocument Data:\n${documentDataSummary}\n\nSections:\n${sectionsSummary}\n\nProvide helpful, concise responses about the document content.`;
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const contextPrompt = buildContext();
      const fullPrompt = `${contextPrompt}\n\nUser question: ${content.trim()}`;

      // Build generic fields for context
      const genericFields: Record<string, Record<string, string>> = {};
      genericSections.forEach((gs) => {
        genericFields[gs.title] = {};
        gs.fields.forEach((field) => {
          if (field.value) {
            genericFields[gs.title][field.label] = field.value;
          }
        });
      });

      const response = await generateApi.generate({
        section: {
          id: 0,
          title: 'Chat Assistant',
          prompt: fullPrompt,
        },
        applicantData: {
          ...applicantData,
          genericFields,
        },
        systemPrompt: systemPrompt || chatSettings.chatSystemPrompt || 'You are a helpful document writing assistant. Provide concise, actionable responses.',
        llmModel: applicantData.llmModel,
      });

      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: response.content || response.generatedContent || 'I apologize, I was unable to generate a response.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
      const errorMessage: ChatMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: `Error: ${err.response?.data?.error || 'Failed to get response. Please try again.'}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleQuickAction = (prompt: string) => {
    sendMessage(prompt);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!chatSettings.enabled) return null;

  return (
    <div className={`fixed z-50 ${POSITION_CLASSES[position]}`}>
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 rounded-full bg-accent text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center hover:scale-105"
          title="Open AI Chat"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className={`${SIZE_CLASSES[size]} bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden transition-all duration-200`}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-accent text-white rounded-t-xl">
            <div className="flex items-center gap-2">
              <MessageCircle size={18} />
              <span className="font-medium text-sm">AI Assistant</span>
              <span className="text-xs opacity-75 bg-white/20 px-2 py-0.5 rounded-full">
                {mode === 'planning' ? 'Planning' : 'Content'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={cycleSize}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
                title={`Size: ${size}`}
              >
                {size === 'large' ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setMode('content')}
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                mode === 'content'
                  ? 'text-accent border-b-2 border-accent'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Content Mode
            </button>
            <button
              onClick={() => setMode('planning')}
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                mode === 'planning'
                  ? 'text-accent border-b-2 border-accent'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Planning Mode
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-gray-400 text-sm py-8">
                <MessageCircle size={32} className="mx-auto mb-2 opacity-50" />
                <p>Ask me anything about your document!</p>
                <p className="text-xs mt-1">Use quick actions below or type your question.</p>
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-accent text-white rounded-br-sm'
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                  <p
                    className={`text-[10px] mt-1 ${
                      msg.role === 'user' ? 'text-white/60' : 'text-gray-400'
                    }`}
                  >
                    {formatTime(msg.timestamp)}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-3 py-2 rounded-bl-sm">
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="px-3 py-2 border-t border-gray-100">
            <div className="flex gap-1.5 flex-wrap">
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action.prompt)}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
                >
                  <action.icon size={12} />
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your document..."
                rows={1}
                className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent max-h-20"
                style={{ minHeight: '36px' }}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                className="p-2 rounded-lg bg-accent text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
